import React, { useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { WorkspacePremium } from "@mui/icons-material";
import { AuthContext } from "../../context/AuthContext";
import { admins, baseURL } from "../../Constants";
import {
    uploadDemo, UploadedDemo, getUpload, getUploadMatch, getUploadPlayerMatches,
    getMyTeamNames, setUploadTeamNames,
} from "../../services/UserDemoService";
import styles from "./UserDemos.module.css";

const ALLOWED = [".dem", ".zip", ".gz", ".zst", ".rar", ".7z", ".tar"];
const MAX_MB = 600;

type Stage = "idle" | "uploading" | "parsing" | "naming" | "error";

interface SideRoster { name: string; players: string[]; }

const formatBytes = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadDemoPage: React.FC = () => {
    const { t } = useTranslation("pages");
    const { user, isBeta } = useContext(AuthContext);
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const abortRef = useRef<AbortController | null>(null);

    const [file, setFile] = useState<File | null>(null);
    const [stage, setStage] = useState<Stage>("idle");
    const [percent, setPercent] = useState(0);
    const [sent, setSent] = useState({ loaded: 0, total: 0 });
    const [error, setError] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);

    // Post-parse team-naming step.
    const [uploaded, setUploaded] = useState<UploadedDemo | null>(null);
    const [side1, setSide1] = useState<SideRoster>({ name: "", players: [] });
    const [side2, setSide2] = useState<SideRoster>({ name: "", players: [] });
    const [team1Name, setTeam1Name] = useState("");
    const [team2Name, setTeam2Name] = useState("");
    const [priorNames, setPriorNames] = useState<string[]>([]);
    const [savingNames, setSavingNames] = useState(false);

    // Beta testers are let in for the dark-launch test window; drop isBeta when uploads go donator-only for real.
    const isDonator = !!user && (user.donator || !!user.teamTier || isBeta || admins.includes(user.steamID));

    const steamLogin = () => { window.location.href = baseURL + "/Auth/steam"; };

    if (!user) {
        return (
            <div className={styles.gateOverlay}>
                <div className={styles.gateCard}>
                    <span className={styles.gateIcon}><WorkspacePremium sx={{ fontSize: 28 }} /></span>
                    <div className={styles.gateTitle}>{t("userDemos.upload.loginGate.title")}</div>
                    <p className={styles.gateBody}>{t("userDemos.upload.loginGate.body")}</p>
                    <button className="btn btn-default" onClick={steamLogin}>
                        <img src="https://steamcommunity-a.akamaihd.net/public/images/signinthroughsteam/sits_large_noborder.png" alt="steamLogo" />
                    </button>
                    <div className={styles.gateDivider}>{t("userDemos.upload.loginGate.or")}</div>
                    <button className={styles.gateSecondary} onClick={() => navigate("/premium")}>
                        {t("userDemos.upload.loginGate.seePremium")}
                    </button>
                </div>
            </div>
        );
    }
    if (!isDonator) {
        return (
            <div className={styles.gateOverlay}>
                <div className={styles.gateCard}>
                    <span className={styles.gateIcon}><WorkspacePremium sx={{ fontSize: 28 }} /></span>
                    <div className={styles.gateTitle}>{t("userDemos.donatorOnly.title")}</div>
                    <p className={styles.gateBody}>{t("userDemos.donatorOnly.body")}</p>
                    <button className={styles.btnPrimary} onClick={() => navigate("/premium")}>
                        {t("userDemos.donatorOnly.cta")}
                    </button>
                </div>
            </div>
        );
    }

    const validate = (f: File): string | null => {
        const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
        if (!ALLOWED.includes(ext)) return t("userDemos.upload.errors.unsupportedType", { types: ALLOWED.join(", ") });
        if (f.size > MAX_MB * 1024 * 1024) return t("userDemos.upload.errors.tooLarge", { max: MAX_MB });
        return null;
    };

    const pickFile = (f: File | null) => {
        setError(null);
        if (!f) return;
        const err = validate(f);
        if (err) { setError(err); setFile(null); return; }
        setFile(f);
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files?.length) pickFile(e.dataTransfer.files[0]);
    };

    const startUpload = async () => {
        if (!file) return;
        setError(null);
        setStage("uploading");
        setPercent(0);
        abortRef.current = new AbortController();
        try {
            const result = await uploadDemo(
                file,
                (p, loaded, total) => {
                    setPercent(p);
                    setSent({ loaded, total });
                    if (p >= 100) setStage("parsing");
                },
                abortRef.current.signal
            );
            // Upload accepted — the parse now runs server-side as a background job, so the request
            // returned as soon as the bytes landed (no more proxy timeouts). Poll for completion.
            setStage("parsing");
            await pollUntilParsed(result.uploadId);
        } catch (err: any) {
            if (axiosIsCancel(err)) { setStage("idle"); return; }
            const serverMsg = err?.response?.data?.error;
            setError(serverMsg || t("userDemos.upload.errors.uploadFailed"));
            setStage("error");
        }
    };

    // Poll the background parse to completion. The upload request returns immediately now, so the
    // parse status lives on the demo record (processing → completed/failed).
    const pollUntilParsed = async (uploadId: string) => {
        const startedAt = Date.now();
        const TIMEOUT_MS = 15 * 60 * 1000;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            await new Promise((r) => setTimeout(r, 2000));
            let demo: UploadedDemo;
            try {
                demo = await getUpload(uploadId);
            } catch {
                if (Date.now() - startedAt > TIMEOUT_MS) break;
                continue; // transient error — keep polling
            }
            if (demo.status === "completed") { await loadNaming(demo); return; }
            if (demo.status === "failed") {
                setError(demo.errorMessage || t("userDemos.upload.errors.parseFailed"));
                setStage("error");
                return;
            }
            if (Date.now() - startedAt > TIMEOUT_MS) break;
        }
        setError(t("userDemos.upload.errors.parseTimeout"));
        setStage("error");
    };

    // After a successful parse, fetch the two rosters + the donator's prior team names so they can
    // name each team while seeing who's on it (sides/clan names can't be known before parsing).
    const loadNaming = async (result: UploadedDemo) => {
        setUploaded(result);
        try {
            const [matches, prior] = await Promise.all([
                getUploadMatch(result.uploadId),
                getMyTeamNames().catch(() => [] as string[]),
            ]);
            const game = matches[0];
            const nameFor = (ids: string[] | undefined, players: any[]) =>
                (ids || []).map((sid) => {
                    const pm = players.find((p) => p.steamId === sid);
                    return pm?.account?.playerName || sid;
                });
            let players: any[] = [];
            if (game) {
                try { players = await getUploadPlayerMatches(result.uploadId, game.gameid); } catch { /* names optional */ }
            }
            const n1 = game?.team1?.teamName || t("userDemos.upload.team", { n: 1 });
            const n2 = game?.team2?.teamName || t("userDemos.upload.team", { n: 2 });
            setSide1({ name: n1, players: nameFor(game?.team1playerids, players) });
            setSide2({ name: n2, players: nameFor(game?.team2playerids, players) });
            setTeam1Name(n1);
            setTeam2Name(n2);
            setPriorNames(prior);
            setStage("naming");
        } catch {
            // Naming is best-effort polish; if anything fails just go straight to the viewer.
            navigate(`/demo/${result.uploadId}`);
        }
    };

    const saveNames = async () => {
        if (!uploaded) return;
        setError(null);
        setSavingNames(true);
        try {
            await setUploadTeamNames(uploaded.uploadId, team1Name.trim(), team2Name.trim());
            navigate(`/demo/${uploaded.uploadId}`);
        } catch (err: any) {
            setError(err?.response?.data?.error || t("userDemos.upload.errors.saveNamesFailed"));
            setSavingNames(false);
        }
    };

    const skipNaming = () => {
        if (uploaded) navigate(`/demo/${uploaded.uploadId}`);
    };

    const cancel = () => {
        abortRef.current?.abort();
        setStage("idle");
        setPercent(0);
    };

    const busy = stage === "uploading" || stage === "parsing";
    const namesInvalid =
        team1Name.trim().length === 0 ||
        team2Name.trim().length === 0 ||
        team1Name.trim().toLowerCase() === team2Name.trim().toLowerCase();

    return (
        <div className="container mt-4" style={{ maxWidth: 720 }}>
            <h3>{t("userDemos.upload.title")}</h3>
            <p className={styles.muted}>{t("userDemos.upload.intro")}</p>

            {!busy && stage !== "naming" && (
                <div
                    className={`p-5 mb-3 text-center ${styles.dropzone} ${dragOver ? styles.dropzoneActive : ""}`}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                >
                    <div style={{ fontSize: "2rem" }}>⬆️</div>
                    <div className="fw-bold">{file ? file.name : t("userDemos.upload.dropzone")}</div>
                    <div className={`small ${styles.muted}`}>
                        {file ? formatBytes(file.size) : t("userDemos.upload.accepted", { types: ALLOWED.join(", "), max: MAX_MB })}
                    </div>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={ALLOWED.join(",")}
                        style={{ display: "none" }}
                        onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                    />
                </div>
            )}

            {error && <div className="alert alert-danger">{error}</div>}

            {stage === "uploading" && (
                <div className="mb-3">
                    <div className={`d-flex justify-content-between small mb-1 ${styles.muted}`}>
                        <span>{t("userDemos.upload.uploading", { percent })}</span>
                        <span>{formatBytes(sent.loaded)} / {formatBytes(sent.total)}</span>
                    </div>
                    <div className="progress" style={{ height: 20 }}>
                        <div className="progress-bar" role="progressbar" style={{ width: `${percent}%` }}>{percent}%</div>
                    </div>
                    <button className="btn btn-outline-secondary btn-sm mt-2" onClick={cancel}>{t("userDemos.upload.cancel")}</button>
                </div>
            )}

            {stage === "parsing" && (
                <div className="mb-3">
                    <div className={`small mb-1 ${styles.muted}`}>{t("userDemos.upload.parsing")}</div>
                    <div className="progress" style={{ height: 20 }}>
                        <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: "100%" }} />
                    </div>
                    <div className={`small mt-1 ${styles.muted}`}>{t("userDemos.upload.parsingSteps")}</div>
                </div>
            )}

            {stage === "naming" && (
                <div className="mb-3">
                    <div className="alert alert-success py-2">{t("userDemos.upload.namingIntro")}</div>
                    <datalist id="priorTeamNames">
                        {priorNames.map((n) => <option key={n} value={n} />)}
                    </datalist>
                    <div className="row g-3">
                        {([[side1, team1Name, setTeam1Name], [side2, team2Name, setTeam2Name]] as const).map(
                            ([side, value, setValue], i) => (
                                <div className="col-md-6" key={i}>
                                    <label className="form-label fw-bold">{t("userDemos.upload.team", { n: i + 1 })}</label>
                                    <input
                                        className="form-control"
                                        list="priorTeamNames"
                                        value={value}
                                        placeholder={t("userDemos.upload.team", { n: i + 1 })}
                                        maxLength={64}
                                        onChange={(e) => setValue(e.target.value)}
                                    />
                                    <ul className={`list-unstyled small mt-2 mb-0 ${styles.muted}`}>
                                        {side.players.map((p) => <li key={p}>· {p}</li>)}
                                    </ul>
                                </div>
                            )
                        )}
                    </div>
                    {namesInvalid && (
                        <div className="text-danger small mt-2">{t("userDemos.upload.namesInvalid")}</div>
                    )}
                    <div className="mt-3 d-flex gap-2">
                        <button className="btn btn-primary" disabled={namesInvalid || savingNames} onClick={saveNames}>
                            {savingNames ? t("userDemos.upload.saving") : t("userDemos.upload.saveAndView")}
                        </button>
                        <button className="btn btn-outline-secondary" disabled={savingNames} onClick={skipNaming}>
                            {t("userDemos.upload.skip")}
                        </button>
                    </div>
                </div>
            )}

            {!busy && stage !== "naming" && (
                <button className="btn btn-primary" disabled={!file} onClick={startUpload}>
                    {t("userDemos.upload.uploadAndParse")}
                </button>
            )}
        </div>
    );
};

// Lightweight cancel check without importing axios just for this.
function axiosIsCancel(err: any): boolean {
    return err && (err.code === "ERR_CANCELED" || err.name === "CanceledError" || err.message === "canceled");
}

export default UploadDemoPage;
